import { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Paper,
  Box,
  Alert,
} from '@mui/material'
import ExportButton from '../../components/ExportButton'
import ProgressBar from '../../components/ProgressBar'
import FieldSelector, { FieldConfiguration, SelectedField } from '../../components/FieldSelector'
import ExportFilters from '../../components/ExportFilters'
import { lightspeedApi } from '../../services/api'
import { useFranchise } from '../../contexts/FranchiseContext'
import { useExportState } from '../../hooks/useExportState'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useAsyncExport } from '../../hooks/useAsyncExport'
import { EXPORT_TYPES, defaultProductsExportState, type ProductsExportState } from '../../types/exportStates'

function LightspeedProducts() {
  usePageTitle('Lightspeed', 'Products Export')
  const { fpCode } = useFranchise()
  const { state: exportState, updateState: updateExportState } = useExportState<ProductsExportState>({
    exportType: EXPORT_TYPES.LIGHTSPEED_PRODUCTS,
    defaultState: defaultProductsExportState
  })
  
  const { productStatus, inventoryFilter, deletedFilter } = exportState

  // Use async export hook
  const {
    loading,
    error: exportError,
    success: exportSuccess,
    progressMessage,
    currentCount,
    startExport
  } = useAsyncExport({
    exportType: 'products'
  })

  const [fieldConfiguration, setFieldConfiguration] = useState<FieldConfiguration | null>(null)
  const [selectedFields, setSelectedFields] = useState<SelectedField[]>([])
  const [localError, setLocalError] = useState<string | null>(null)

  // Combine errors from hook and local validation
  const error = exportError || localError

  // Load field configuration on mount
  useEffect(() => {
    const loadFieldConfiguration = async () => {
      try {
        const response = await lightspeedApi.getProductFields()
        setFieldConfiguration(response.data)
      } catch (error) {
        console.error('Failed to load field configuration:', error)
        setLocalError('Failed to load export field configuration')
      }
    }

    loadFieldConfiguration()
  }, [])

  // Auto-enable 'deleted_at' field when deleted filter is 'include' or 'only'
  useEffect(() => {
    if (!fieldConfiguration || !deletedFilter) return

    // Check if we should auto-enable the deleted_at field
    if (deletedFilter === 'include' || deletedFilter === 'only') {
      // Find the deleted_at field in the configuration
      const deletedAtField = fieldConfiguration.fields.find(f => f.id === 'deleted_at')

      if (deletedAtField) {
        // Check if it's already in the selected fields
        const isAlreadySelected = selectedFields.some(f => f.id === 'deleted_at' && f.enabled)

        if (!isAlreadySelected) {
          // Add or enable the deleted_at field
          setSelectedFields(prev => {
            const existingField = prev.find(f => f.id === 'deleted_at')

            if (existingField) {
              // Field exists but is disabled, enable it
              return prev.map(f =>
                f.id === 'deleted_at'
                  ? { ...f, enabled: true }
                  : f
              )
            } else {
              // Field doesn't exist in selection, add it as enabled
              const newField: SelectedField = {
                ...deletedAtField,
                enabled: true,
                order: prev.length
              }
              return [...prev, newField]
            }
          })
        }
      }
    }
  }, [deletedFilter, fieldConfiguration, selectedFields])

  const handleFieldsChange = useCallback((fields: SelectedField[]) => {
    setSelectedFields(fields)
  }, [])

  // Helper function to analyze field requirements
  const analyzeFieldRequirements = useCallback((fields: SelectedField[]) => {
    const requirements = {
      needsInventory: false,
      needsOutlets: false,
      needsLineItems: false
    }

    for (const field of fields) {
      if (field.requires_inventory) {
        requirements.needsInventory = true
      }
      if (field.requires_outlets) {
        requirements.needsOutlets = true
      }
      if (field.requires_line_items) {
        requirements.needsLineItems = true
      }
    }

    return requirements
  }, [])

  const handleExport = async () => {
    if (!fpCode) {
      setLocalError('Please select a franchise partner')
      return
    }

    setLocalError(null)

    // Build export parameters
    const fieldIds = selectedFields.map(f => f.id).join(',')

    const params: Record<string, any> = {
      fp_code: fpCode,
      format: 'xlsx',
      status: productStatus,
      inventory_filter: inventoryFilter,
      deleted_filter: deletedFilter
    }

    // Only add selected_fields if user has made a selection
    if (fieldIds) {
      params['selected_fields'] = fieldIds
    }

    // Start the async export
    await startExport(params)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!fpCode && (
          <Alert severity="info">
            Please select a franchise partner from the dropdown above to continue.
          </Alert>
        )}

          {fpCode && (
            <>
              <ExportFilters
                title="Filters"
                filters={[
                  {
                    id: 'status',
                    label: 'Product Status',
                    type: 'select',
                    options: [
                      { value: 'all', label: 'All Statuses' },
                      { value: 'active', label: 'Active Only' },
                      { value: 'inactive', label: 'Inactive Only' }
                    ],
                    value: productStatus,
                    onChange: (value: string) => updateExportState({ productStatus: value })
                  },
                  {
                    id: 'inventory',
                    label: 'Product Inventory',
                    type: 'select',
                    options: [
                      { value: 'all', label: 'All Inventory' },
                      { value: 'onhand', label: 'On-hand Only' }
                    ],
                    value: inventoryFilter,
                    onChange: (value: string) => updateExportState({ inventoryFilter: value })
                  },
                  {
                    id: 'deleted',
                    label: 'Deleted Status',
                    type: 'select',
                    options: [
                      { value: 'exclude', label: 'Exclude deleted' },
                      { value: 'include', label: 'Include deleted' },
                      { value: 'only', label: 'Deleted only' }
                    ],
                    value: deletedFilter,
                    onChange: (value: string) => updateExportState({ deletedFilter: value })
                  }
                ]}
              />

              <Paper sx={{ p: 3 }}>
                <FieldSelector
                  configuration={fieldConfiguration}
                  onFieldsChange={handleFieldsChange}
                  exportType="lightspeed_products"
                />
              </Paper>

          {error && (
            <Alert severity="error" onClose={() => setLocalError(null)}>
              {error}
            </Alert>
          )}

          {exportSuccess && (
            <Alert severity="success">
              {exportSuccess}
            </Alert>
          )}

          {loading && (
            <Box>
              <ProgressBar
                indeterminate
                message={progressMessage || "Exporting products... This may take a few minutes for large catalogs."}
              />
              {currentCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Records processed: {currentCount.toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

              <ExportButton
                onClick={handleExport}
                loading={loading}
                disabled={!fpCode || selectedFields.length === 0}
                text="Export Products"
              />

              {selectedFields.length === 0 && (
                <Alert severity="warning">
                  Please select at least one field to export.
                </Alert>
              )}

            </>
          )}
      </Box>
    </Box>
  )
}

export default LightspeedProducts